import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import {
  Button,
  ListGroup,
  Table,
  Image,
  Stack,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { ImNewTab } from 'react-icons/im';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import defaultAvatar from '@assets/default-avatar.png';
import DeleteRowIcon from '@components/Icons/DeleteRowIcon/DeleteRowIcon';
import {
  ComHubHeadersEnum,
  PendingReviewModelType,
  Review,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import { ReviewRequestModel } from '@company/models/ReviewRequestModel';
import DATE_FORMATS from '@constants/dateFormat';
import { formatTimeOrDateNow } from '@utils/DateUtils';
import { BASE_PATH } from '@utils/Environment';
import { useCompanyContext } from '@company/state/companyContext';
import './styles.css';
import { NO, YES } from '@constants/various';

interface ReviewRequestsTableProps {
  reviewRequests: Review[];
  title: string;
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onSeeReviewRequest: (review: Review) => void;
  onDismissItems?: (item: Review[]) => void;
  newCount?: number;
}

function ReviewRequestsTable({
  title,
  reviewRequests,
  onShowWorkerProfile,
  onSeeReviewRequest,
  onDismissItems,
  newCount,
}: ReviewRequestsTableProps) {
  const { indicators } = useCompanyContext();
  // Use newCount prop if provided, otherwise fall back to indicators
  const displayCount = newCount ?? indicators?.reviewRequestsCount;
  const headers = [
    ComHubHeadersEnum.EVENT_NAME,
    ComHubHeadersEnum.WORKER_NAME,
    ComHubHeadersEnum.POSITION,
    ComHubHeadersEnum.DATES_WORKED,
    ComHubHeadersEnum.RESPONDED,
    ComHubHeadersEnum.TH_GIG,
    ComHubHeadersEnum.WORKER_REVIEWED_DATE,
    ComHubHeadersEnum.ACTIONS_COLUMN,
  ];

  const newReviewRequests =
    reviewRequests?.filter((reviewRequest) => reviewRequest?.userState?.new) ||
    [];

  const handleDismissItem = (itemsToDismiss: Review[]) => {
    if (onDismissItems) {
      onDismissItems(itemsToDismiss);
    }
  };

  const handleShowWorkerProfile = (request: ReviewRequestModel) => {
    const workerId = request.getWorkerId();
    if (onShowWorkerProfile && workerId) {
      onShowWorkerProfile({
        workerId,
        jobAssignmentId: request.jobAssignmentId,
        jobId: request.jobId,
      });
    }
  };

  return (
    <div className="th-table">
      <header className="th-table-header d-flex align-items-center flex-wrap">
        <Link
          to="?tab=received-requests"
          className="text-decoration-none align-self-end"
          style={{ color: 'inherit' }}
        >
          <h4 className="text-start">
            {title}{' '}
            {displayCount !== undefined && (
              <b data-testid="new-counter">({displayCount})</b>
            )}
          </h4>
        </Link>
        {newReviewRequests?.length ? (
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>Dismiss all new that&apos;s shown</Tooltip>}
          >
            <Button
              type="button"
              variant="light"
              className="ms-auto fw-bold d-flex align-items-center"
              style={{ fontSize: isMobile ? '12px' : '14px' }}
              onClick={() => handleDismissItem(newReviewRequests)}
            >
              {newReviewRequests.length} new
              <DeleteRowIcon className="ms-2" size={isMobile ? 18 : 22} />
            </Button>
          </OverlayTrigger>
        ) : null}
      </header>
      <hr style={{ margin: '10px 0' }} />
      {reviewRequests?.length ? (
        <>
          <BrowserView>
            <Table>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* RENDER COLUMS BY ROW */}
                {reviewRequests.map((reviewRequest) => {
                  const request = new ReviewRequestModel(reviewRequest);
                  const hasRatingDateCreated = request.getDateCreated() !== '';
                  let requestCompleted = '-';
                  if (
                    PendingReviewModelType.TH_GIG_PENDING_REVIEW !==
                    request.reviewType
                  ) {
                    requestCompleted = request.completed ? YES : NO;
                  }

                  return (
                    <tr
                      key={request.getUniqueKey()}
                      className={`${request.isNew() ? 'new' : ''}`}
                    >
                      <td>
                        {reviewRequest.jobId ? (
                          <Link
                            to={`${BASE_PATH}jobs/${request.jobId}`}
                            target="_blank"
                            className="cursor-point event-name-link"
                            style={{ color: 'currentcolor' }}
                          >
                            {request.eventName}
                            <ImNewTab
                              color="var(--yellow-dark-20)"
                              className="ms-2 event-name-link-icon"
                            />
                          </Link>
                        ) : (
                          <span>{request.eventName}</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleShowWorkerProfile(request)}
                          className={`${request.isNew() ? 'new' : ''}`}
                        >
                          {request.getWorkerName()}
                        </button>
                      </td>
                      <td className="text-capitalize">
                        {request.getPosition()}
                      </td>
                      <td>{request.getWorkedDateRange()}</td>
                      <td>{requestCompleted}</td>
                      <td>
                        {PendingReviewModelType.TH_GIG_PENDING_REVIEW ===
                        request.reviewType
                          ? YES
                          : NO}
                      </td>
                      <td className="date">
                        <span className="me-3">
                          {hasRatingDateCreated
                            ? formatTimeOrDateNow(request.getDateCreated())
                            : '-'}
                        </span>
                      </td>
                      {/* BUTTONS FOR ROW ACTIONS */}
                      <td className="actions">
                        <Button
                          onClick={() => onSeeReviewRequest(request)} // if fails put back reviewRequest
                          className="action-btn me-2"
                        >
                          open
                        </Button>
                        {PendingReviewModelType.TH_GIG_PENDING_REVIEW !==
                        request.reviewType ? (
                          <button
                            type="button"
                            disabled={!request.isNew()}
                            style={{
                              cursor: !request.isNew()
                                ? 'not-allowed'
                                : 'pointer',
                            }}
                          >
                            <DeleteRowIcon
                              onClick={() => handleDismissItem([request])} // if fails put back reviewRequest
                              color={!request.isNew() ? 'var(--silver)' : ''}
                            />
                          </button>
                        ) : (
                          <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </BrowserView>
          <MobileView>
            <ListGroup variant="flush">
              {reviewRequests.map((reviewRequest) => {
                const request = new ReviewRequestModel(reviewRequest);
                const hasRatingDateCreated = request.getDateCreated() !== '';
                return (
                  <ListGroup.Item
                    key={request.getUniqueKey()}
                    className="ps-0 pe-0 border-0 d-flex gap-2 align-items-center"
                    onClick={(e) => {
                      // Check if the click target is not within the Button or DeleteRowIcon
                      const dismissButton =
                        e.currentTarget.querySelector('.dismiss-button');
                      if (!dismissButton?.contains(e.target as Node)) {
                        onSeeReviewRequest(request); // if fails put back reviewRequest
                      }
                    }}
                  >
                    <Image
                      src={
                        reviewRequest.companyRating?.reviewer?.imageUrl ||
                        defaultAvatar
                      }
                      alt="reviewer profile picture"
                      width={40}
                      height={40}
                      roundedCircle
                    />
                    <Stack>
                      <span
                        className="text-truncate"
                        style={{ maxWidth: '200px' }}
                      >
                        {reviewRequest.eventName}
                      </span>
                      <span>{request.getWorkerName()}</span>
                      <span>{request.getPosition()}</span>
                    </Stack>
                    <Stack className="ms-auto justify-content-between align-items-end">
                      <span>
                        {hasRatingDateCreated &&
                          format(
                            new Date(request.getDateCreated()),
                            DATE_FORMATS.SHORT_DATE_FORMAT
                          )}
                      </span>
                      <Button
                        variant="light"
                        size="sm"
                        className="p-1 dismiss-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissItem([reviewRequest]); // if fails put back reviewRequest
                        }}
                        disabled={!request.isNew()}
                      >
                        <DeleteRowIcon
                          color={!request.isNew() ? 'var(--silver)' : ''}
                        />
                      </Button>
                    </Stack>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </MobileView>
        </>
      ) : (
        <p className="mb-2 th-fs-14">No new notifications.</p>
      )}
    </div>
  );
}

export default ReviewRequestsTable;
